import { ApolloLink, HttpLink, Observable } from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import { fromPromise } from '@apollo/client/link/utils'

import { makeAirbrakeErrorLink } from '@ui/lib/airbrake/apolloErrorLink'

import { awsWafStageFetch } from '@web-app/lib/awsWafStage'
import type { Token } from '@web-app/lib/ironSession'

import type { MintResult } from './tokenMachine'

const NEXT_PUBLIC_GRAPHQL_API_ENDPOINT =
  process.env.NEXT_PUBLIC_GRAPHQL_API_ENDPOINT

if (!NEXT_PUBLIC_GRAPHQL_API_ENDPOINT) {
  throw new Error(
    'Environment variable NEXT_PUBLIC_GRAPHQL_API_ENDPOINT is not defined!',
  )
}

// The backend's exact bad-credential message (app_schema.rb
// unauthorized_object), distinct from resolver faults.
// TODO(backend-ask): a structured extensions.code would end prose-matching.
export const isAuthSignatureError = (
  graphQLErrors: ReadonlyArray<{ message?: string }> | undefined,
): boolean =>
  Boolean(
    graphQLErrors?.some((error) =>
      error.message?.includes('hidden due to permissions'),
    ),
  )

type AuthErrorLinkDeps = {
  forceMint: () => Promise<MintResult>
  getToken: () => Token | undefined
  reload: () => void
}

// On the auth signature, mint and forward once; Apollo never re-delivers a
// retried operation's errors, so no loop. A factory: each client owns its mint.
export const makeAuthErrorLink = ({
  forceMint,
  getToken,
  reload,
}: AuthErrorLinkDeps) =>
  onError(({ graphQLErrors, operation, forward }) => {
    if (!isAuthSignatureError(graphQLErrors)) return
    // A rejected tokenless flight is expected, not evidence of death.
    if (!operation.getContext().hadToken) return

    return fromPromise(forceMint()).flatMap((result) => {
      if (result === 'dead' || result === 'mismatch') {
        reload()
        // The page is unloading; never complete into an awaiting success path.
        return new Observable(() => undefined)
      }
      // 'ok': retry with the fresh token. 'unavailable': retry as-is — the
      // repeat failure propagates, standing in for the original error.
      const token = getToken()
      operation.setContext(({ headers = {} }) => ({
        headers: token
          ? {
              ...headers,
              Authorization: `Bearer ${token.replace('Bearer ', '')}`,
            }
          : headers,
      }))
      return forward(operation)
    })
  })

// Variables verified ID/enum-only, safe on error notices; everything else
// sends key names only. Review the generated Input type before adding.
const VARIABLE_SAFE_OPERATIONS = [
  'CreateCashAdvanceApplication',
  'RunCashAdvanceEvaluation',
  'SubmitCashAdvanceApplication',
  'AssignBankAccountToCashAdvanceApplication',
  'RepayCashAdvanceRequest',
  'UnlinkBankAccount',
  'RelinkPaymentAccount',
  'CreateLinkToken',
]

// Stateless links, shared by every client.
export const errorLink = makeAirbrakeErrorLink({
  variableSafeOperations: VARIABLE_SAFE_OPERATIONS,
})

const supportsKeepalive = () => {
  if (typeof window === 'undefined') {
    return false
  }

  try {
    new Request('_', { keepalive: true })
    return true
  } catch (error) {
    console.error('Error checking keepalive support:', error)
    return false
  }
}

export const conditionalFetchOptionsLink = new ApolloLink(
  (operation, forward) => {
    if (operation.operationName === 'PublishWebEvent' && supportsKeepalive()) {
      operation.setContext({
        fetchOptions: {
          // Lets the event request outlive the page (sendBeacon replacement).
          keepalive: true,
        },
      })
    }

    return forward(operation)
  },
)

export const httpLink = new HttpLink({
  uri: NEXT_PUBLIC_GRAPHQL_API_ENDPOINT,
  fetch: awsWafStageFetch,
})
